import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { isPushSupported, subscribeToPush, getExistingSubscription } from '../lib/push';

/**
 * One-time post-login push opt-in dialog.
 * Shown when:
 *   - browser supports push,
 *   - user is logged in,
 *   - permission is `default`,
 *   - localStorage key `asap_push_prompted` isn't set yet.
 */
export default function PushPermissionDialog({ user }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isPushSupported()) return;
    let cancelled = false;
    (async () => {
      try {
        const prompted = window.localStorage.getItem('asap_push_prompted');
        if (prompted) return;
        if (Notification.permission !== 'default') return;
        const existing = await getExistingSubscription();
        if (existing) return;
        // Defer a bit so it doesn't fight with route-load animations
        setTimeout(() => { if (!cancelled) setOpen(true); }, 1200);
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
  }, [user]);

  const markPrompted = () => {
    try { window.localStorage.setItem('asap_push_prompted', '1'); } catch {/* ignore */}
  };

  const handleEnable = async () => {
    if (submitting) return;
    setSubmitting(true);
    const result = await subscribeToPush();
    markPrompted();
    setSubmitting(false);
    setOpen(false);
    if (!result.ok) {
      // Silent on failure; the user can always re-enable from Settings.
      console.debug('[asap] push subscribe rejected:', result.reason);
    }
  };

  const handleLater = () => {
    markPrompted();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 p-5 animate-fade-in"
      onClick={handleLater}
      data-testid="push-permission-dialog"
    >
      <div
        className="w-full max-w-[400px] bg-white rounded-3xl p-6 animate-scale-in shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="w-12 h-12 rounded-2xl bg-[#B6E600]/15 flex items-center justify-center">
            <Bell size={22} className="text-[#3F5200]" strokeWidth={2.4} />
          </div>
          <button
            onClick={handleLater}
            data-testid="push-dialog-close"
            className="p-1 -mt-1 -mr-1 text-[#9B9EA3] active:scale-90 transition-transform"
          >
            <X size={20} />
          </button>
        </div>
        <h3 className="text-[20px] font-extrabold text-[#111111] tracking-tight">
          Stay in the loop
        </h3>
        <p className="text-[13.5px] text-[#6B6E73] leading-relaxed mt-1.5">
          Turn on notifications and never miss a join request, a DM, or a safety update — even when ASAP is closed.
        </p>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            onClick={handleLater}
            data-testid="push-dialog-later"
            className="py-3 rounded-full bg-[#F1F0EA] text-sm font-semibold text-[#111111] active:scale-[0.97] transition-transform"
          >
            Maybe later
          </button>
          <button
            onClick={handleEnable}
            disabled={submitting}
            data-testid="push-dialog-enable"
            className="py-3 rounded-full bg-[#B6E600] text-sm font-bold text-[#111111] active:scale-[0.97] transition-transform disabled:opacity-50"
          >
            {submitting ? 'Enabling…' : 'Enable'}
          </button>
        </div>
      </div>
    </div>
  );
}
